/**
 * KpiWidget — a single configurable KPI card for the Common Dashboard
 * Framework. Renders straight from a /api/dashboard/v2/kpis metric payload:
 *   { key, label, value, unit, format, higher_is_better, error }
 *
 * Promotes the visual language of components/oim/KpiCard (title / big value /
 * trend badge) into a module-agnostic widget with: loading skeleton, empty
 * state, error state, optional drill-down (onClick), and an optional trend
 * (previous-period comparison, wired in a later increment).
 */
import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle, Info } from 'lucide-react';
import { formatMetricValue, unitForMetric } from '../utils/format';
import { CARD, ACCENT_DOT, LABEL } from '../ui';

const FLAT_THRESHOLD = 0.5; // percent — below this, render "flat"

function TrendBadge({ trend, higherIsBetter, trendLabel }) {
  if (trend === null || trend === undefined || Number.isNaN(trend)) return null;
  if (Math.abs(trend) < FLAT_THRESHOLD) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500" title="Effectively unchanged">
        <Minus className="h-3 w-3" />
        flat
      </span>
    );
  }
  const up = trend > 0;
  // higher_is_better === null means neutral metric — color by sign only.
  const isGood = higherIsBetter === null || higherIsBetter === undefined ? up : up === !!higherIsBetter;
  const color = isGood ? 'text-green-700' : 'text-amber-700';
  const sign = up ? '+' : '−';
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}
      title={`${sign}${Math.abs(trend).toFixed(2)}% ${trendLabel || 'vs previous period'}`}
    >
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {sign}
      {Math.abs(trend).toFixed(1)}%
    </span>
  );
}

function Shell({ accent, clickable, onClick, children }) {
  const interactive = clickable
    ? 'cursor-pointer transition-shadow duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400/60'
    : 'transition-shadow duration-200 hover:shadow-md';
  return (
    <div
      className={`${CARD} px-4 py-3.5 ${interactive}`}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick?.(e) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {children}
    </div>
  );
}

function Label({ accent, children }) {
  const dot = ACCENT_DOT[accent] || ACCENT_DOT.blue;
  return (
    <div className="flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
      <h3 className={`${LABEL} truncate`}>{children}</h3>
    </div>
  );
}

export default function KpiWidget({
  metric,
  title,
  loading = false,
  onClick,
  accent = 'blue',
  trend = null,
  trendLabel,
}) {
  const label = title || metric?.label || '—';
  // Acronym tiles (TRIR, LTIFR, LWC Rate, ...) say nothing to a reader who does
  // not already know the term. The registry has carried a definition all along;
  // this exposes it on demand rather than spending permanent card space on it.
  const description = metric?.description;
  const [showInfo, setShowInfo] = useState(false);

  if (loading) {
    return (
      <Shell accent={accent}>
        <div className="mb-3 h-3 w-2/3 animate-pulse rounded bg-gray-100" />
        <div className="h-7 w-1/2 animate-pulse rounded bg-gray-100" />
      </Shell>
    );
  }

  if (metric?.error) {
    return (
      <Shell accent="red">
        <Label accent="red">{label}</Label>
        <div className="mt-2 flex items-center gap-1.5 text-sm text-red-500">
          <AlertCircle className="h-4 w-4" />
          Unavailable
        </div>
      </Shell>
    );
  }

  const value = metric?.value;
  const isEmpty = value === null || value === undefined;
  const display = formatMetricValue(value, metric?.format);
  const unit = unitForMetric(metric);
  const clickable = typeof onClick === 'function';

  return (
    <Shell accent={accent} clickable={clickable} onClick={onClick}>
      <div className="flex items-start justify-between gap-2">
        <Label accent={accent}>{label}</Label>
        {description && (
          <button
            type="button"
            // The card itself may open a drill-down; without this the info
            // toggle would also fire it.
            onClick={(e) => { e.stopPropagation(); setShowInfo((v) => !v); }}
            onKeyDown={(e) => e.stopPropagation()}
            aria-expanded={showInfo}
            aria-label={`What is ${label}?`}
            title={`What is ${label}?`}
            className={`shrink-0 rounded p-0.5 transition-colors focus:outline-none
                        focus:ring-2 focus:ring-blue-400/60
                        ${showInfo ? 'text-blue-600' : 'text-gray-300 hover:text-gray-500'}`}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="mt-2 flex items-baseline gap-1.5">
        <span className={`text-[26px] font-semibold leading-none tabular-nums ${isEmpty ? 'text-gray-300' : 'text-gray-900'}`}>
          {display}
        </span>
        {!isEmpty && unit && <span className="text-sm font-medium text-gray-400">{unit}</span>}
      </div>

      {/* Normalization basis for rate metrics (OSHA per 200k / India per 1M hrs). */}
      {metric?.basis && (
        <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">{metric.basis}</div>
      )}

      {showInfo && (
        <div className="mt-2 rounded-md bg-gray-50 px-2.5 py-2 text-[11px] leading-relaxed text-gray-600">
          {description}
        </div>
      )}

      <div className="mt-2 flex min-h-[16px] items-center justify-between">
        {isEmpty ? (
          <span className="text-[11px] text-gray-300">no data</span>
        ) : (
          <span />
        )}
        <TrendBadge trend={trend} higherIsBetter={metric?.higher_is_better} trendLabel={trendLabel} />
      </div>
    </Shell>
  );
}
